"use client";

import { Form, Formik, type FormikHelpers } from "formik";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

import {
  EVENT_SURVEY_OUTCOME_CALCULATED,
  SURVEY_SUBMIT,
} from "@shared/constants";

import getSurveyOutcome from "@server/front-end-api/getSurveyOutcome";
import patchSurveyStep from "@server/front-end-api/patchSurveyStep";
import postEvent from "@server/front-end-api/postEvent";
import postSurveyStep from "@server/front-end-api/postSurveyStep";

import DataLayer from "@client/classes/data-layer/data-layer";
import createSurveyCookie from "@client/cookies/createSurveyCookie";
import getSurveyCookie from "@client/cookies/getSurveyCookie";

import type Survey from "@packages/types/survey";
import { validationSchema } from "@packages/types/yup/debt-payoff-calculator";

/**
 * Note: This is a "client" component because Formik creates a React context and that's something you can only do
 *       client side.
 *
 * Note: `initialValues` gets defined "server-side", and gets passed to this component on the "client-side" so we
 *       can use LocalStorage to get/save field values. However, we don't want to save `step`, `stepName`, or
 *       `submitAction` in LocalStorage, because these values are specific to each individual step and we don't
 *       ever want to accidentally overwrite them "client-side".
 *
 *       On render, we loop through `initalValues` and replace the "default" values with "saved" values. We don't loop
 *       through LocalStorage because it can contain fields that don't belong to this step. Formik doesn't care
 *       if a field exists or not, it sends what's inside `initialValues` so please be careful when modifying
 *       `initalValues`.
 *
 * @see https://formik.org/docs/api/formik#initialvalues-values
 */

type Props = {
  children: React.ReactNode;
  initialValues: Survey.StepValues;
  redirectPath: string;
  submitMethod: Survey.SubmitMethod;
};

type formattedValuesType = {
  debts: { [key: string]: any }[];
  submitMethod: string;
};

const SURVEY_TYPE = "debt-payoff-calculator";
/**
 * Submit Survey to Front-End API
 */
async function submitForm(redirectPath: string, values: formattedValuesType) {
  let submitFormRedirectPath;
  let cookie = await getSurveyCookie(SURVEY_TYPE);
  let surveyData;
  if (cookie) {
    const { data, error } = await patchSurveyStep(values, cookie.surveyId);
    if (error) {
      throw new Error(error);
    }
    surveyData = data;
  } else {
    const { data, error } = await postSurveyStep(values, SURVEY_TYPE);
    if (error) {
      throw new Error(error);
    }
    surveyData = data;
  }
  const surveyId = cookie?.surveyId
    ? cookie.surveyId
    : String(surveyData?.survey.survey_id);

  if (!cookie) {
    createSurveyCookie({ surveyId: surveyId, surveyType: SURVEY_TYPE });
    cookie = await getSurveyCookie(SURVEY_TYPE);
  }

  const surveyOutcome = await getSurveyOutcome(surveyId);

  if (
    surveyOutcome.error ==
    "Error: Monthly payment for a debt is too low to cover interest."
  ) {
    submitFormRedirectPath = `${redirectPath}?surveyId=${cookie?.surveyId}&error="payments-too-low"`;
    return submitFormRedirectPath;
  } else if (surveyOutcome.error) {
    throw new Error(surveyOutcome.error);
  }

  // trigger "benefits survey submitted" event
  DataLayer.events.trigger(EVENT_SURVEY_OUTCOME_CALCULATED, {
    surveyId: cookie?.surveyId,
    surveyType: SURVEY_TYPE,
  });
  submitFormRedirectPath = `${redirectPath}?surveyId=${cookie?.surveyId}`;

  return submitFormRedirectPath;
}

// TODO: The formatValues function is a workaround for a speedy release, so that the backend doesn't have to update right before trying to release.
function formatValues(values: any) {
  const debtObject: any = {
    debts: [],
  };

  values.debts.forEach((debt: { [key: string]: string }) =>
    debtObject.debts.push({
      debt_type: debt.debtType,
      amount: parseValue(debt.amount),
      interest_rate: parseValue(debt.interestRate),
      monthly_payment: parseValue(debt.monthlyPayment),
    }),
  );

  for (let i = debtObject.debts.length; i < 20; i++) {
    debtObject.debts.push({
      debt_type: "",
      amount: 0,
      interest_rate: 0,
      monthly_payment: 0,
    });
  }

  return debtObject;
}

function parseValue(value: string) {
  const valueWithoutCommas = value.replaceAll(",", "");
  let parsedValue = Math.round(parseFloat(valueWithoutCommas));
  return parsedValue;
}

export default function DebtPayoffCalculatorFormik({
  children,
  initialValues,
  redirectPath,
  submitMethod,
  ...props
}: Props) {
  const router = useRouter();

  const handleSubmit = async (
    values: Survey.StepValues,
    formikHelpers: FormikHelpers<Survey.StepValues>,
  ) => {
    try {
      const formattedValues = formatValues(values);
      // handle form submission
      redirectPath = await submitForm(redirectPath, formattedValues);
      // // move user to next step
      router.push(redirectPath);
    } catch (e) {
      postEvent("error", {
        error: {
          error_message: `An error occurred while attempting to submit the Debt Payoff Calculator.`,
          error_object: "Debt Payoff Calculator",
          error_type: "Front-End API",
          path: window.location.href,
        },
      });

      formikHelpers.setStatus("error");
    }
  };

  useEffect(() => {
    if (submitMethod !== SURVEY_SUBMIT && redirectPath) {
      router.prefetch(redirectPath);
    }
  }, [redirectPath, router, SURVEY_SUBMIT, submitMethod, initialValues]);

  return (
    <Formik
      {...props}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      <Form
        className="form"
        noValidate={true}
        data-spec-debt-payoff-calculator-form
        method="POST"
        action=""
      >
        {children}
      </Form>
    </Formik>
  );
}
